import { Box, Button, RadioGroup as ChakraRadioGroup, FormControl, FormLabel, Grid, Radio, Stack, useDisclosure } from '@chakra-ui/react'
import type { InsurerDiscount, Item } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect, useState } from 'react'

import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { getLogo } from '@/utils/companiesLogos'

import { CompanyCheckbox } from './company-checkbox'
import { CompanyCheckboxGroup } from './CompanyCheckboxGroup'

export type CompaniesSelectionProps = {
  allInsurers: Item[]
  allInsurersDiscounts: InsurerDiscount[]
  insurersPath: string
  insurersDiscountsPath?: string
  columnsCount?: number
}

export const CompaniesSelection = ({
  allInsurers,
  allInsurersDiscounts,
  insurersPath,
  insurersDiscountsPath,
  columnsCount = 1
}: CompaniesSelectionProps) => {
  const { onToggle: onToggleDiscounts, isOpen: isOpenDiscounts } = useDisclosure()
  const [insurersDiscounts, setInsurersDiscounts] = useState<InsurerDiscount[]>(allInsurersDiscounts)
  const { isDisabled } = useInsuranceParams()

  useEffect(() => {
    setInsurersDiscounts(allInsurersDiscounts)
  }, [allInsurersDiscounts])

  const methods = useFormContext()
  const {
    watch,
    setValue,
    getValues,
    getFieldState
  } = methods

  const { error: discountsError } = getFieldState(insurersDiscountsPath ?? '')

  const selectedInsurers = (watch(insurersPath) ?? []) as number[]
  const isSelectedAllInsurers = allInsurers.every(x => selectedInsurers.includes(x.id))

  return (
    <FormControl sx={{ gap: 8 }} isInvalid={!!discountsError}>
      <Stack sx={{
        flexDirection: columnsCount > 1 ? 'column' : 'row',
        width: '100%',
        justifyContent: columnsCount > 1 ? 'flex-start' : 'space-between',
      }} >
        <FormLabel>Vybrané pojišťovny</FormLabel>

        {insurersDiscounts.length > 0 &&
          <Box>
            <Button
              variant='link'
              whiteSpace='nowrap'
              onClick={onToggleDiscounts}
              isDisabled={selectedInsurers.length == 0}
            >
              {isOpenDiscounts ? 'Zavřít slevy' : 'Nastavit slevy'}
            </Button>
          </Box>
        }
      </Stack>

      <ChakraRadioGroup
        onChange={(e) => {
          setValue(insurersPath, e == '1'
            ? [...allInsurers?.map(x => x.id) ?? []]
            : [])
        }}
        value={isSelectedAllInsurers ? '1' : '0'}
        isDisabled={isDisabled}
      >
        <Stack
          direction='row'
          spacing={{
            base: 4,
            lg: 8,
          }}
        >
          <Radio value='1'>
            Všechny dostupné
          </Radio>
          <Radio value='0'>
            Vlastní výběr
          </Radio>
        </Stack>
      </ChakraRadioGroup>

      {(!isSelectedAllInsurers || isOpenDiscounts) && (
        <CompanyCheckboxGroup name={insurersPath}>
          <Grid
            sx={{
              gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
              width: '100%',
              columnGap: 4,
              rowGap: 1,
              alignItems: 'start',
            }}
          >
            {
              allInsurers
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(({ id: insurerId, name: insurerName }) => {
                  const discounts = insurersDiscounts?.find(x => x.insurerId == insurerId)?.discounts?.filter(x => x.editK1)

                  return (
                    <CompanyCheckbox
                      key={insurerId}
                      value={insurerId}
                      insurerId={insurerId}
                      logo={getLogo(insurerId)}
                      discounts={discounts}
                      showDiscounts={isOpenDiscounts && selectedInsurers.includes(insurerId)}
                      onDiscountChange={(insuranceCategoryDiscountId, value) => {
                        if (!insurersDiscountsPath) return

                        const current = (getValues(insurersDiscountsPath) ?? []) as InsurerDiscount[]
                        // sleva pojistovny nemusi byt ve formulari (napr. pojistovna neni vybrana / formular se mezitim resetoval) - pak hodnotu do formulare nezapisujeme
                        const disc = current.find(x => x.insurerId == insurerId)?.discounts?.find(x => x.insuranceCategoryDiscountId == insuranceCategoryDiscountId)
                        if (!disc) return
                        disc.discount = value

                        setValue(insurersDiscountsPath, [...current])
                      }}
                      onVoucherChange={(insuranceCategoryDiscountId, value) => {
                        if (!insurersDiscountsPath) return

                        const current = (getValues(insurersDiscountsPath) ?? []) as InsurerDiscount[]
                        // sleva pojistovny nemusi byt ve formulari (napr. pojistovna neni vybrana / formular se mezitim resetoval) - pak hodnotu do formulare nezapisujeme
                        const disc = current.find(x => x.insurerId == insurerId)?.discounts?.find(x => x.insuranceCategoryDiscountId == insuranceCategoryDiscountId)
                        if (!disc) return
                        disc.voucher = value

                        setValue(insurersDiscountsPath, [...current])
                      }}
                      isDisabled={isDisabled}
                    >
                      {insurerName}
                    </CompanyCheckbox>
                  )
                })}
          </Grid>
        </CompanyCheckboxGroup>
      )}
    </FormControl>
  )
}

export default CompaniesSelection